// =============================================
// MusicMembership — plans, current tier, and signed paperwork retrieval
// =============================================
// Shows the music caps for each plan (from site_service_plans), the user's current
// plan, a mock upgrade/switch, and the retrievable legal records the user has signed
// (the initial agreement + every per-upload ownership consent).
// =============================================
import React, { useMemo, useState } from 'react';
import { FiCheck, FiFileText, FiShield, FiUploadCloud, FiX } from 'react-icons/fi';
import { getTableData } from '../db-viewer/data/dataAccess';
import { saveRow } from '../../lib/profileApi'; // plan-switch writes users.plan_id (works for account owners)
import { getAcceptances, AcceptanceRecord } from '../../lib/musicLegal';
import { getMusicEntitlement, countOwnedSongs, sumOwnedBytes, MB } from '../../lib/musicEntitlements';

interface Props {
  userId: number;
  onChanged: () => void;
}

interface PlanRow {
  planId: number;
  name: string;
  code: string;
  monthly: number;
  songs: number;
  mb: number;
  video: boolean;
  adSupported: boolean;
}

function fmtCap(n: number): string {
  if (n >= 999999) return 'Unlimited';
  if (n >= 1000) return (n / 1000).toFixed(n % 1000 ? 1 : 0) + 'k';
  return String(n);
}

function capLabel(used: number, limit: number): string {
  return limit === Infinity ? `${used} (unlimited)` : `${used} / ${limit}`;
}

export const MusicMembership: React.FC<Props> = ({ userId, onChanged }) => {
  const [busyPlan, setBusyPlan] = useState<number | null>(null);
  const [viewDoc, setViewDoc] = useState<AcceptanceRecord | null>(null);
  const [tick, setTick] = useState(0);

  const currentPlanId = useMemo(() => {
    // eslint-disable-next-line eqeqeq
    const u = getTableData('users').find((x: any) => x.user_id == userId);
    return u?.plan_id != null ? Number(u.plan_id) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, tick]);

  const plans: PlanRow[] = useMemo(() => getTableData('site_service_plans')
    .filter((p: any) => p.is_deleted != 1 && p.is_active != 0) // eslint-disable-line eqeqeq
    .map((p: any) => ({
      planId: Number(p.plan_id),
      name: p.plan_name,
      code: p.plan_code,
      monthly: Number(p.monthly_price) || 0,
      songs: Number(p.max_music_songs) || 0,
      mb: Number(p.max_music_mb) || 0,
      video: Number(p.can_upload_video) === 1,
      adSupported: p.plan_code === 'FREE',
    }))
    .sort((a: PlanRow, b: PlanRow) => a.monthly - b.monthly), [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const records = useMemo(() => getAcceptances(userId), [userId, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Current usage vs caps (the song/storage meter) ----
  const ent = useMemo(() => getMusicEntitlement(userId), [userId, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const songCount = useMemo(() => countOwnedSongs(userId), [userId, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const usedMb = useMemo(() => sumOwnedBytes(userId) / MB, [userId, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const songPct = ent.maxSongs === Infinity ? 0 : Math.min(100, (songCount / ent.maxSongs) * 100);
  const mbPct = ent.maxMb === Infinity ? 0 : Math.min(100, (usedMb / ent.maxMb) * 100);

  const switchPlan = async (planId: number) => {
    setBusyPlan(planId);
    await saveRow('users', userId, { plan_id: planId }, 'user_id');
    setBusyPlan(null);
    setTick(t => t + 1);
    onChanged();
  };

  return (
    <div className="mm-wrap">
      {/* Your usage — song + storage meter */}
      <h3 className="mm-section-title">Your usage</h3>
      <div className="music-caps">
        <div className="music-cap">
          <div className="music-cap-label">Songs <span>{capLabel(songCount, ent.maxSongs)}</span></div>
          <div className="music-cap-bar"><div className="music-cap-fill" style={{ width: `${songPct}%` }} /></div>
        </div>
        <div className="music-cap">
          <div className="music-cap-label">Storage <span>{usedMb.toFixed(0)} MB{ent.maxMb === Infinity ? '' : ` / ${ent.maxMb} MB`}</span></div>
          <div className="music-cap-bar"><div className="music-cap-fill" style={{ width: `${mbPct}%` }} /></div>
        </div>
        {ent.adSupported && (
          <div className="music-cap-note">
            Free: {ent.baseSongs} songs without ads, {ent.maxSongs} with ads. Video needs a paid plan.
          </div>
        )}
      </div>

      {/* Plans */}
      <h3 className="mm-section-title">Membership plans</h3>
      <p className="mm-section-sub">Bigger plans lift your song cap and storage, remove ads, and unlock bulk upload &amp; video.</p>
      <div className="mm-plans">
        {plans.map(p => {
          const current = p.planId === currentPlanId;
          return (
            <div key={p.planId} className={`mm-plan ${current ? 'current' : ''}`}>
              <div className="mm-plan-name">{p.name}</div>
              <div className="mm-plan-price">{p.monthly === 0 ? 'Free' : `$${p.monthly.toFixed(2)}`}<span>{p.monthly === 0 ? '' : '/mo'}</span></div>
              <ul className="mm-plan-feats">
                <li><FiCheck /> {fmtCap(p.songs)} songs</li>
                <li><FiCheck /> {fmtCap(p.mb)} MB storage</li>
                <li>{p.adSupported ? '🔊 Ad-supported' : '🚫 No ads'}</li>
                <li>{p.code === 'FREE' ? 'Single-file upload' : <><FiUploadCloud /> Bulk upload</>}</li>
                <li>{p.video ? '🎬 Video allowed' : 'Audio only'}</li>
              </ul>
              {current ? (
                <button className="mm-plan-btn current" disabled>Current plan</button>
              ) : (
                <button className="mm-plan-btn" onClick={() => switchPlan(p.planId)} disabled={busyPlan != null}>
                  {busyPlan === p.planId ? 'Switching…' : 'Switch to this plan'}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="mm-mock-note">Switching is a functional mock — no real payment is processed.</p>

      {/* Signed paperwork */}
      <h3 className="mm-section-title mm-records-title"><FiShield /> Your signed paperwork</h3>
      <p className="mm-section-sub">Every agreement and per-upload ownership certification you've signed is kept here for your records.</p>
      {records.length === 0 ? (
        <p className="music-empty">No agreements signed yet.</p>
      ) : (
        <div className="mm-records">
          {records.map(r => (
            <div key={r.acceptanceId} className="mm-record">
              <FiFileText className="mm-record-icon" />
              <div className="mm-record-main">
                <span className="mm-record-title">{r.title}</span>
                <span className="mm-record-meta">
                  {r.type === 'initial_agreement' ? 'Service agreement' : `Upload consent · ${r.trackCount ?? '?'} track(s)`}
                  {' · '}v{r.version}
                  {' · '}signed “{r.signatureName}”
                  {' · '}{(r.acceptedAt || '').slice(0, 16).replace('T', ' ')}
                </span>
              </div>
              <button className="mm-record-view" onClick={() => setViewDoc(r)}>View</button>
            </div>
          ))}
        </div>
      )}

      {viewDoc && (
        <div className="mlg-overlay" role="dialog" aria-modal="true" onClick={() => setViewDoc(null)}>
          <div className="mlg-modal" onClick={e => e.stopPropagation()}>
            <div className="mlg-head">
              <FiFileText className="mlg-head-icon" />
              <h3>{viewDoc.title}</h3>
              <button className="mlg-close" onClick={() => setViewDoc(null)}><FiX /></button>
            </div>
            <div className="mlg-body">
              {viewDoc.body.split('\n\n').map((para, i) => <p key={i} className="mlg-para">{para}</p>)}
            </div>
            <p className="mm-record-foot">
              Signed by <strong>{viewDoc.signatureName}</strong> on {(viewDoc.acceptedAt || '').slice(0, 16).replace('T', ' ')} · version {viewDoc.version}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicMembership;
